import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import {
  DEFAULT_THEME_PREFERENCE,
  THEME_PREFERENCE_LEGACY_STORAGE_KEYS,
  THEME_PREFERENCE_STORAGE_KEY,
  type ResolvedTheme,
  type ThemePreference,
} from './tokens'

type ThemeContextValue = {
  preference: ThemePreference
  resolvedTheme: ResolvedTheme
  setPreference: (preference: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const isThemePreference = (value: string | null): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'system'

const applyResolvedTheme = (theme: ResolvedTheme) => {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.setAttribute('data-theme', theme)
  root.style.colorScheme = theme
}

const clearStoredThemePreference = () => {
  if (typeof window === 'undefined') return
  try {
    const direct = window.localStorage.getItem(THEME_PREFERENCE_STORAGE_KEY)
    if (isThemePreference(direct)) {
      window.localStorage.removeItem(THEME_PREFERENCE_STORAGE_KEY)
    }
    for (const legacyKey of THEME_PREFERENCE_LEGACY_STORAGE_KEYS) {
      const legacyValue = window.localStorage.getItem(legacyKey)
      if (isThemePreference(legacyValue)) {
        window.localStorage.removeItem(legacyKey)
      }
    }
  } catch {
    // no-op: storage can be blocked in private mode
  }
}

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const preference = DEFAULT_THEME_PREFERENCE
  const resolvedTheme: ResolvedTheme = 'light'

  useEffect(() => {
    clearStoredThemePreference()
    applyResolvedTheme(resolvedTheme)
  }, [resolvedTheme])

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      resolvedTheme,
      setPreference: () => undefined,
    }),
    [preference, resolvedTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used inside ThemeProvider')
  return context
}
